import React, { useState } from "react";
import {
  ChevronRight,
  Briefcase,
  MessageSquarePlus,
  Shield,
  Lock,
  CircleCheck,
  Circle,
} from "lucide-react";
import catImage from "@/assets/cat.png";
import { Button } from "@/components/ui/button";
import { PhonePreview } from "@/components/common/PhonePreview";
import {
  useBookingCheckinSettings,
  useUpdateBookingCheckinSettings,
} from "./hooks/useBookingCheckinSettings";
import { useCheckinServiceLists } from "./hooks/useBookingCheckinServiceLists";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import { SortableServiceItem } from "@/components/common/SortableServiceList";
import { HotelService } from "@/types/hotelService";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ContactInfoTab } from "./tabs/ContactInfoTab";
import { BookingInfoTab } from "./tabs/BookingInfoTab";
import { RoomUpgradeTab } from "./tabs/RoomUpgradeTab";
import { DocumentTab } from "./tabs/DocumentTab";
import { FinalTab } from "./tabs/FinalTab";
import { AdditionalInfoTab } from "./tabs/AdditionalInfoTab";
import { PaymentTab } from "./tabs/PaymentTab";
import { CustomQuestionsTab } from "./tabs/CustomQuestionsTab";
import {
  CheckinSettingsResponse,
  UpdateCheckinSettings,
} from "@/types/booking-checkin-settings";
import { useAllServices } from "@/hooks/useHotelServices";
import { CheckinQuestion } from "@/types/checkin-question";
import { useTranslation } from "react-i18next";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Label = ({ children, htmlFor, className }: any) => (
  <label htmlFor={htmlFor} className={`text-sm font-medium ${className || ""}`}>
    {children}
  </label>
);

enum CheckinTabs {
  CONTACT_INFO = "contact-info",
  BOOKING_INFO = "booking-info",
  ROOM_UPGRADE = "room-upgrade",
  DOCUMENT = "document",
  ADDITIONAL_INFO = "additional-info",
  PAYMENT = "payment",
  CUSTOM_QUESTIONS = "custom-questions",
  FINAL = "final",
}

const TAB_LABELS = {
  [CheckinTabs.CONTACT_INFO]: "Contact info",
  [CheckinTabs.BOOKING_INFO]: "Booking info",
  [CheckinTabs.ROOM_UPGRADE]: "Room upgrade",
  [CheckinTabs.DOCUMENT]: "Document",
  [CheckinTabs.ADDITIONAL_INFO]: "Additional Info",
  [CheckinTabs.PAYMENT]: "Payment",
  [CheckinTabs.CUSTOM_QUESTIONS]: "Custom questions",
  [CheckinTabs.FINAL]: "Final",
};

export const CheckinSettingsPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<CheckinTabs>(
    CheckinTabs.CONTACT_INFO,
  );
  const hotelId = useCurrentHotelId();
  const { data: settings, isLoading } = useBookingCheckinSettings();
  const updateMutation = useUpdateBookingCheckinSettings();
  const {
    checkinRecommended,
    checkinFeatured,
    bookingUpsell,
    updateCheckinRecommended,
    updateCheckinFeatured,
    updateBookingUpsell,
  } = useCheckinServiceLists(hotelId || "");

  const { data: allServicesResponse } = useAllServices(hotelId || "");
  const allServices = allServicesResponse?.data || [];

  const [localSettings, setLocalSettings] =
    useState<CheckinSettingsResponse | null>(null);

  const [upsellServices, setUpsellServices] = useState<SortableServiceItem[]>(
    [],
  );
  const [recommendedServices, setRecommendedServices] = useState<
    SortableServiceItem[]
  >([]);
  const [featuredServices, setFeaturedServices] = useState<
    SortableServiceItem[]
  >([]);

  // Initialize local settings when data is fetched
  React.useEffect(() => {
    if (settings) {
      setLocalSettings(settings);
    }
  }, [settings]);

  const currentSettings = localSettings ?? settings ?? null;

  const handleSettingsChange = React.useCallback<
    React.Dispatch<React.SetStateAction<CheckinSettingsResponse | null>>
  >(
    (updater) => {
      setLocalSettings((previousSettings) => {
        const baseSettings = previousSettings ?? settings ?? null;

        return typeof updater === "function" ? updater(baseSettings) : updater;
      });
    },
    [settings],
  );

  // Sync service lists
  React.useEffect(() => {
    if (bookingUpsell.data) {
      setUpsellServices(
        bookingUpsell.data.map((s: HotelService) => ({
          id: s.id,
          serviceId: s.id,
          name: s.name,
        })),
      );
    }
  }, [bookingUpsell.data]);

  React.useEffect(() => {
    if (checkinRecommended.data) {
      setRecommendedServices(
        checkinRecommended.data.map((s: HotelService) => ({
          id: s.id,
          serviceId: s.id,
          name: s.name,
        })),
      );
    }
  }, [checkinRecommended.data]);

  React.useEffect(() => {
    if (checkinFeatured.data) {
      setFeaturedServices(
        checkinFeatured.data.map((s: HotelService) => ({
          id: s.id,
          serviceId: s.id,
          name: s.name,
        })),
      );
    }
  }, [checkinFeatured.data]);

  const handleSave = async () => {
    if (!currentSettings) return;

    // Update main settings
    const updateData: UpdateCheckinSettings = {
      ...currentSettings,
      questions: currentSettings.questions?.map((q: CheckinQuestion) => ({
        question: q.question,
        order: q.order,
      })),
      customAdditionalFields: (
        currentSettings.customAdditionalFields ?? []
      ).map((field, order) => ({
        label: field.label.trim(),
        order,
        placeholder: field.placeholder?.trim() || undefined,
      })),
    };
    delete (updateData as any)["hotelId"];
    delete (updateData as any)["id"];
    // hot fix: we need to change naming here, since frontend works with naming `questions` and backend `checkinQuestions`
    // so to avoid duplication here we just delete this array and fix in the future
    delete (updateData as any)["checkinQuestions"];
    (updateData as any).availableServices = [];
    (updateData as any).unavailableServices = [];

    updateMutation.mutate(updateData);

    // Update Lists
    if (hotelId) {
      updateBookingUpsell.mutate(upsellServices.map((s) => s.serviceId));
      updateCheckinRecommended.mutate(
        recommendedServices.map((s) => s.serviceId),
      );
      updateCheckinFeatured.mutate(featuredServices.map((s) => s.serviceId));
    }

    setActiveTab(CheckinTabs.CONTACT_INFO);
  };

  const handleNextStep = () => {
    const tabs = Object.values(CheckinTabs);
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1]);
    }
  };

  const handlePreviousStep = () => {
    const tabs = Object.values(CheckinTabs);
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1]);
    }
  };

  const visibleCustomQuestions =
    currentSettings?.enabledCustomQuestions === true
      ? (currentSettings?.questions ?? []).filter(
          (question: CheckinQuestion) => question.question.trim().length > 0,
        )
      : [];

  if (isLoading) {
    return <div className="p-12">Loading...</div>;
  }

  return (
    <div className="flex h-screen flex-col">
      {/* Main Content */}
      <div className="flex h-full flex-1 overflow-hidden">
        {/* Left Section - Settings */}
        <div className="flex h-full flex-1 flex-col overflow-y-auto p-6">
          <h1 className="text-2xl font-normal">
            Settings that determine how and when a guest can check-in in the
            app:
          </h1>

          {/* Tab Navigation */}
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as CheckinTabs)}
            className="mt-5 w-full"
          >
            <TabsList className="text-foreground h-9 w-full items-stretch gap-1 rounded-lg bg-[#FBFAFF] px-1 py-1.5 shadow-sm">
              {Object.entries(TAB_LABELS).map(([key, label]) => (
                <TabsTrigger
                  key={key}
                  value={key}
                  className="flex flex-1 items-center justify-center self-stretch rounded-md border border-transparent bg-transparent px-2 py-1 text-xs leading-none font-medium text-[#0A0A0A] shadow-none transition-colors hover:bg-[#EDE9FE]/40 data-[state=active]:border-[#C7BCFF] data-[state=active]:bg-[#F2F0FF] data-[state=active]:shadow-none"
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Tab Content */}
          <div className="mt-6 pr-40 pl-10">
            {activeTab === CheckinTabs.CONTACT_INFO && <ContactInfoTab />}
            {activeTab === CheckinTabs.BOOKING_INFO && (
              <BookingInfoTab
                settings={currentSettings}
                setSettings={handleSettingsChange}
                upsellServices={upsellServices}
                setUpsellServices={setUpsellServices}
                allServices={allServices}
              />
            )}
            {activeTab === CheckinTabs.ROOM_UPGRADE && (
              <RoomUpgradeTab
                settings={currentSettings}
                setSettings={handleSettingsChange}
              />
            )}
            {activeTab === CheckinTabs.DOCUMENT && (
              <DocumentTab
                settings={currentSettings}
                setSettings={handleSettingsChange}
              />
            )}
            {activeTab === CheckinTabs.ADDITIONAL_INFO && (
              <AdditionalInfoTab
                settings={currentSettings}
                setSettings={handleSettingsChange}
              />
            )}
            {activeTab === CheckinTabs.PAYMENT && (
              <PaymentTab
                settings={currentSettings}
                setSettings={handleSettingsChange}
              />
            )}
            {activeTab === CheckinTabs.CUSTOM_QUESTIONS && (
              <CustomQuestionsTab
                settings={currentSettings}
                setSettings={handleSettingsChange}
              />
            )}
            {activeTab === CheckinTabs.FINAL && (
              <FinalTab
                settings={currentSettings}
                setSettings={handleSettingsChange}
                recommendedServices={recommendedServices}
                setRecommendedServices={setRecommendedServices}
                featuredServices={featuredServices}
                setFeaturedServices={setFeaturedServices}
                allServices={allServices}
              />
            )}
          </div>

          <div className="mt-auto flex justify-center gap-4 p-6">
            {activeTab !== CheckinTabs.CONTACT_INFO && (
              <Button
                variant="new_outline"
                size="lg"
                onClick={handlePreviousStep}
              >
                Previous Step
              </Button>
            )}
            {activeTab !== CheckinTabs.CONTACT_INFO && (
              <Button variant="new_default" size="lg" onClick={handleSave}>
                Save
              </Button>
            )}
            {activeTab !== CheckinTabs.FINAL && (
              <Button variant="new_outline" size="lg" onClick={handleNextStep}>
                Next Step
              </Button>
            )}
          </div>
        </div>

        {/* Right Section - Phone Preview */}
        <div className="flex w-[450px] flex-col items-center border-l bg-gray-50 p-6">
          <div className="mb-4 flex gap-2">
            <Button variant="new_outline" size="sm">
              Original
            </Button>
            <Button variant="new_outline" size="sm">
              Preview screen
            </Button>
          </div>
          <div className="mb-4 text-center">
            <h3 className="font-semibold">Guest App Preview</h3>
            <p className="text-xs text-gray-500">
              {activeTab === CheckinTabs.CONTACT_INFO && "Step 1/8"}
              {activeTab === CheckinTabs.BOOKING_INFO && "Step 2/8"}
              {activeTab === CheckinTabs.ROOM_UPGRADE && "Step 3/8"}
              {activeTab === CheckinTabs.DOCUMENT && "Step 4/8"}
              {activeTab === CheckinTabs.ADDITIONAL_INFO && "Step 5/8"}
              {activeTab === CheckinTabs.PAYMENT && "Step 6/8"}
              {activeTab === CheckinTabs.CUSTOM_QUESTIONS && "Step 7/8"}
              {activeTab === CheckinTabs.FINAL && "Step 8/8"}
            </p>
          </div>
          <PhonePreview title="Pre Check-In" containerClassName="min-h-[650px]">
            <div className="space-y-4 overflow-y-auto bg-white p-4">
              {activeTab === CheckinTabs.CONTACT_INFO && (
                <>
                  <h2 className="text-lg font-semibold">Contact Information</h2>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">First Name</Label>
                      <Input placeholder="First Name" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Last Name</Label>
                      <Input placeholder="Last Name" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Email Address</Label>
                      <Input placeholder="Email" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Phone Number</Label>
                      <Input placeholder="+1 234-567-8901" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Date of Birth</Label>
                      <Input type="date" className="mt-1" />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch id="consent" />
                      <Label htmlFor="consent" className="text-xs">
                        I consent to the processing of personal data
                      </Label>
                    </div>
                  </div>
                  <Button className="mt-4 w-full">Next →</Button>
                </>
              )}

              {activeTab === CheckinTabs.BOOKING_INFO && (
                <div className="space-y-4">
                  {currentSettings?.isShowRoomCategoryPhotos ? (
                    <div className="mb-6 flex items-center justify-center rounded-lg bg-gray-100 p-8">
                      <img
                        src={catImage}
                        alt="Booking Placeholder"
                        className="max-h-32 object-contain mix-blend-multiply"
                      />
                    </div>
                  ) : null}
                  <h2 className="font-handwriting mb-6 text-center text-xl font-semibold">
                    We&apos;ve found your Booking Info
                  </h2>

                  {/* Dates */}
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-gray-500">
                      Booking Dates, Check-in time
                    </p>
                    <p className="text-sm">11.06.2025 - 16.06.2025 | 3:00 PM</p>
                  </div>
                  <div className="h-px bg-gray-200" />

                  {/* Services */}
                  {currentSettings?.isShowAdditionalServices
                    ? upsellServices.map((service) => (
                        <div key={service.id}>
                          <div className="flex items-center justify-between py-2">
                            <div className="flex items-center gap-3">
                              <Briefcase className="h-5 w-5 text-gray-600" />
                              <span className="text-sm font-medium">
                                {service.name}
                              </span>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          </div>
                          <div className="h-px bg-gray-200" />
                        </div>
                      ))
                    : null}

                  {/* Room/Guests */}
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-gray-500">
                      Room type, Guests amount
                    </p>
                    <p className="text-sm">Ordinary | 2 Adults, 1 Child</p>
                  </div>
                  <div className="h-px bg-gray-200" />

                  {/* Comments */}
                  {currentSettings?.isShowCommentsPreferencesRequests && (
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm font-medium">
                        Comments/Preferences/Requests
                      </span>
                      <MessageSquarePlus className="h-5 w-5 text-gray-400" />
                    </div>
                  )}

                  <Button className="mt-4 w-full bg-black text-white hover:bg-gray-800">
                    Next &gt;
                  </Button>
                </div>
              )}

              {activeTab === CheckinTabs.ROOM_UPGRADE &&
                currentSettings?.isShowRoomUpgrade && (
                  <div className="space-y-4">
                    <div className="relative mb-4 flex items-center justify-center rounded-lg bg-gray-100 p-8">
                      <img
                        src={catImage}
                        alt="Room Upgrade"
                        className="max-h-32 object-contain mix-blend-multiply"
                      />
                      <div className="absolute top-2 right-2 rounded bg-black px-2 py-1 text-[10px] text-white">
                        Upgraded
                      </div>
                    </div>

                    <h2 className="font-handwriting text-lg font-semibold">
                      Upgrade to Luxury Sea View
                    </h2>

                    <div className="flex gap-2">
                      <div className="rounded border border-black px-2 py-1 text-xs font-medium">
                        12m2 {`=>`} 23m2
                      </div>
                      <div className="rounded border border-black px-2 py-1 text-xs font-medium">
                        €90 per night
                      </div>
                    </div>

                    <p className="text-xs text-gray-600">
                      Our usual family room with a small kitchen and a garden
                      view.
                    </p>

                    {currentSettings?.isShowRoomFacilities ? (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold">
                          &lt; Room Facilities
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {[
                            "king-size bed",
                            "microwave",
                            "tv",
                            "Hairdryer",
                            "Wifi",
                            "mini bar",
                            "fridge",
                            "air conditioner",
                            "free parking",
                            "safe",
                            "sofa",
                            "no pets",
                          ].map((facility) => (
                            <span
                              key={facility}
                              className="rounded-full bg-black px-2 py-1 text-[10px] text-white"
                            >
                              {facility}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    <div className="border-t pt-2">
                      <p className="text-[10px] leading-tight text-gray-500">
                        <span className="underline">
                          Room Upgrades are based upon availability
                        </span>
                        , we will get back to you shortly to confirm your
                        upgrade.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Button className="h-10 w-full bg-black text-white hover:bg-gray-800">
                        Request Upgrade €90
                      </Button>
                      <Button
                        variant="outline"
                        className="h-10 w-full border-black text-black hover:bg-gray-100"
                      >
                        Skip
                      </Button>
                    </div>
                  </div>
                )}

              {activeTab === CheckinTabs.DOCUMENT && (
                <div className="flex flex-col space-y-6">
                  <div className="flex-1 space-y-4">
                    {currentSettings?.isShowDocumentVerification && (
                      <>
                        {currentSettings.isVerifyIdCard && (
                          <div className="flex items-center gap-3">
                            <Circle className="h-5 w-5 text-gray-400" />
                            <span className="text-sm font-medium">ID card</span>
                          </div>
                        )}
                        {currentSettings.isVerifyPassport && (
                          <div className="flex items-center gap-3">
                            <CircleCheck className="h-5 w-5 fill-black text-black text-white" />
                            <span className="text-sm font-medium">
                              Passport
                            </span>
                          </div>
                        )}
                        {currentSettings.isVerifyDrivingLicense && (
                          <div className="flex items-center gap-3">
                            <Circle className="h-5 w-5 text-gray-400" />
                            <span className="text-sm font-medium">
                              Driving License
                            </span>
                          </div>
                        )}
                        {currentSettings.isVerifyBirthCertificate && (
                          <div className="flex items-center gap-3">
                            <Circle className="h-5 w-5 text-gray-400" />
                            <span className="text-sm font-medium">
                              Birth certificate
                            </span>
                          </div>
                        )}
                        {currentSettings.isVerifyResidencePermit && (
                          <div className="flex items-center gap-3">
                            <Circle className="h-5 w-5 text-gray-400" />
                            <span className="text-sm font-medium">
                              Residence permit
                            </span>
                          </div>
                        )}

                        <div className="mt-12 space-y-4 text-center">
                          <p className="font-handwriting mx-auto w-2/3 text-center text-sm">
                            Please select the appropriate document type
                          </p>
                          <div className="flex justify-center gap-2 text-gray-300">
                            <Shield className="h-4 w-4" />
                            <Lock className="h-4 w-4" />
                            <Shield className="h-4 w-4" />
                          </div>
                        </div>
                      </>
                    )}

                    {currentSettings?.isShowInviteGuests && (
                      <div className="space-y-2 rounded-lg border border-gray-200 p-3">
                        <p className="text-sm font-medium">Invite guests</p>
                        <p className="text-xs text-gray-500">
                          Add the details of the guests staying with you.
                        </p>
                        <Button variant="outline" className="w-full">
                          Add guest
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="mt-auto space-y-2">
                    <Button className="h-10 w-full bg-black text-white hover:bg-gray-800">
                      Next &gt;
                    </Button>
                    {currentSettings?.skipDocuments ? (
                      <Button
                        variant="outline"
                        className="h-10 w-full border-black text-black hover:bg-gray-100"
                      >
                        Skip
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="h-10 w-full border-black text-black hover:bg-gray-100"
                      >
                        &lt; Back
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {activeTab === CheckinTabs.ADDITIONAL_INFO && (
                <div className="space-y-6">
                  {currentSettings?.enabledAdditionalInformation &&
                  currentSettings?.enabledHomeAddress ? (
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase">
                        Home Address
                      </Label>
                      <Input
                        value={currentSettings.homeAddress || ""}
                        readOnly
                        placeholder="Address"
                        className="border-black"
                      />
                    </div>
                  ) : null}
                  {currentSettings?.enabledAdditionalInformation &&
                  currentSettings?.enabledIBAN ? (
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase">
                        IBAN
                      </Label>
                      <Input
                        value={currentSettings.iban || ""}
                        readOnly
                        placeholder="FR00 12345678901234567890123"
                        className="border-black"
                      />
                    </div>
                  ) : null}
                  {currentSettings?.enabledAdditionalInformation
                    ? (currentSettings.customAdditionalFields ?? []).map(
                        (field) => (
                          <div key={field.id} className="flex flex-col gap-2">
                            <Label className="text-xs font-semibold uppercase">
                              {field.label}
                            </Label>
                            <Input
                              readOnly
                              placeholder={
                                field.placeholder ||
                                t(
                                  "common.checkinSettings.additionalInfo.customField.previewPlaceholder",
                                  { field: field.label.toLowerCase() },
                                )
                              }
                              className="border-black"
                            />
                          </div>
                        ),
                      )
                    : null}

                  <div className="mt-auto space-y-2 pt-8">
                    <Button className="h-10 w-full bg-black text-white hover:bg-gray-800">
                      Next &gt;
                    </Button>
                    {currentSettings?.skipAdditionalInformation && (
                      <Button
                        variant="outline"
                        className="h-10 w-full border-black text-black hover:bg-gray-100"
                      >
                        Skip
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {activeTab === CheckinTabs.PAYMENT &&
                currentSettings?.enabledPaymentForAccomodation && (
                  <div className="space-y-4">
                    {/* Header/Icon */}
                    <div className="pt-4 text-center">
                      {/* Using a simple icon here, ideally use a specific Payment icon */}
                      <Briefcase className="mx-auto mb-3 h-8 w-8" />
                      <h2 className="text-lg font-semibold">
                        Payment for Stay
                      </h2>
                      <p className="mx-auto mt-1 max-w-[250px] text-center text-xs text-gray-500">
                        By paying in advance, you will be able to expedite your
                        check-in upon arrival!
                      </p>
                    </div>

                    {/* Booking Details Row */}
                    <div className="mt-4 flex items-center justify-between border-t border-b py-2 text-[10px] text-gray-500">
                      <div>
                        <p>Booking Dates, Check-in time</p>
                        <p className="font-medium text-black">
                          11.06.2025 - 16.06.2025 | 3:00 PM
                        </p>
                      </div>
                      <div className="text-sm font-semibold text-black">
                        €800
                      </div>
                    </div>

                    {/* Line Items */}
                    <div className="space-y-2 py-2">
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <span>→</span>
                          <span>Baby Crib</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span>€20</span>
                          <span className="text-gray-400">×</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <span>→</span>
                          <div>
                            <p>Tourist tax</p>
                            <p className="text-[8px] text-gray-400">
                              Official taxes
                            </p>
                          </div>
                        </div>
                        <span>€20</span>
                      </div>
                    </div>

                    {/* Total */}
                    <div className="flex justify-end pt-4 pb-8">
                      <div className="text-right">
                        <span className="mr-2 text-[10px] text-gray-500">
                          Amount due
                        </span>
                        <span className="text-lg font-bold">€840</span>
                      </div>
                    </div>

                    {/* Security Badges */}
                    <div className="flex items-end justify-between">
                      <p className="text-[10px] text-gray-500">
                        Quick. Easy. Secure.
                      </p>
                      <div className="flex gap-1 text-gray-300">
                        <Shield className="h-3 w-3" />
                        <Lock className="h-3 w-3" />
                        <Shield className="h-3 w-3" />
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-auto space-y-2">
                      <Button className="h-10 w-full bg-black text-white hover:bg-gray-800">
                        Pay &gt;
                      </Button>
                      {currentSettings?.enabledPayLater && (
                        <Button
                          variant="outline"
                          className="h-10 w-full border-black text-black hover:bg-gray-100"
                        >
                          Pay Later
                        </Button>
                      )}
                    </div>
                  </div>
                )}

              {activeTab === CheckinTabs.CUSTOM_QUESTIONS && (
                <div className="space-y-4">
                  <div className="space-y-4">
                    {visibleCustomQuestions.length > 0
                      ? visibleCustomQuestions.map(
                          (question: CheckinQuestion) => (
                            <Input
                              key={question.id || question.order}
                              value={question.question}
                              readOnly
                              className="border-black"
                            />
                          ),
                        )
                      : null}
                  </div>

                  <div className="mt-auto space-y-2 pt-8">
                    <Button className="h-10 w-full bg-black text-white hover:bg-gray-800">
                      Next &gt;
                    </Button>
                    {currentSettings?.skipCustomQuestions && (
                      <Button
                        variant="outline"
                        className="h-10 w-full border-black text-black hover:bg-gray-100"
                      >
                        Skip
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {activeTab === CheckinTabs.FINAL && (
                <div className="space-y-4">
                  {/* Completion Icon */}
                  <div className="mb-2 flex justify-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded border-2 border-black">
                      <CircleCheck className="h-8 w-8 text-black" />
                    </div>
                  </div>

                  {/* Completion Message */}
                  <div className="space-y-1 text-center">
                    <h2 className="text-xl font-semibold">
                      Check-In Completed
                    </h2>
                    <p className="text-xs text-gray-600">
                      We will verify all data and get back to you soon.
                    </p>
                  </div>

                  {/* What's Next Section */}
                  <div className="space-y-2">
                    <h3 className="text-base font-semibold underline">
                      What&apos;s next?
                    </h3>
                    <p className="text-xs text-gray-600">
                      In our app we have offers to make your stay comfortable
                      and vacation memorable.
                    </p>
                  </div>

                  {/* Recommended Services */}
                  {currentSettings?.isShowRecommendedServicesOnCompleted && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">
                          Recommended{" "}
                          <span className="underline">Services</span>
                        </h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 bg-black px-2 text-xs text-white hover:bg-gray-800"
                        >
                          View All
                        </Button>
                      </div>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {[
                          {
                            name: "Breakfast in the room",
                            price: "15€/pers",
                            tag: "Room",
                          },
                          {
                            name: "Relaxing Massage",
                            price: "60€/pers",
                            tag: "SPA",
                          },
                          {
                            name: "Romantic Dinner for 2",
                            price: "15€/per",
                            tag: "Restaurant",
                          },
                        ].map((service, idx) => (
                          <div
                            key={idx}
                            className="w-32 flex-shrink-0 space-y-1 rounded-lg border border-black p-2"
                          >
                            <div className="w-fit rounded bg-black px-1.5 py-0.5 text-[8px] text-white">
                              {service.tag}
                            </div>
                            <p className="text-[10px] leading-tight font-medium">
                              {service.name}
                            </p>
                            <p className="text-[9px] text-gray-500">
                              {service.price}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Featured Activities */}
                  {currentSettings?.isShowFeaturedActivitiesOnCompleted && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">
                          Featured <span className="underline">Activities</span>
                        </h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 bg-black px-2 text-xs text-white hover:bg-gray-800"
                        >
                          View All
                        </Button>
                      </div>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {[
                          {
                            name: "Dinner at Eiffel Tower",
                            price: "35€/pers",
                            tag: "For couples",
                          },
                          {
                            name: "Walking Tour at Montmartre",
                            price: "35€/pers",
                            tag: "Guide",
                          },
                          { name: "Louvre", price: "35€/per", tag: "Museum" },
                        ].map((activity, idx) => (
                          <div
                            key={idx}
                            className="w-32 flex-shrink-0 space-y-1 rounded-lg border border-black p-2"
                          >
                            <div className="w-fit rounded bg-black px-1.5 py-0.5 text-[8px] text-white">
                              {activity.tag}
                            </div>
                            <p className="text-[10px] leading-tight font-medium">
                              {activity.name}
                            </p>
                            <p className="text-[9px] text-gray-500">
                              {activity.price}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="space-y-2 pt-2">
                    <div className="flex gap-2">
                      <Button className="h-10 flex-1 bg-black text-white hover:bg-gray-800">
                        Explore
                      </Button>
                      <Button className="h-10 flex-1 bg-black text-white hover:bg-gray-800">
                        WhatsApp
                      </Button>
                    </div>
                    <Button
                      variant="outline"
                      className="h-10 w-full border-black text-black hover:bg-gray-100"
                    >
                      My Booking Info
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </PhonePreview>
        </div>
      </div>
    </div>
  );
};
